'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview RouteMap — FRE-770 spatial route map atop the Saved calendar
 * view. Pure SVG (no Mapbox GL) so the bundle stays small.
 *
 * - Dark teal dot-grid background at 8% opacity.
 * - Dashed amber polyline (2px, dasharray 6 4) connecting up to 3 stops.
 * - Numbered amber circles with a 44px touch target on each stop.
 * - Top-left glass chip: `TONIGHT · {n} STOPS · {dist} MI`.
 * - Reduced motion: skips the polyline draw-on-mount animation.
 */
import React, { useId, useMemo, useState } from 'react';
import { GlassPill } from '../GlassPill';
import { Coord } from '../Coord';
import { lociAmber } from '../../../theme/design-tokens';
import { cltAccentTokens } from '../../../tokens/colors';
import { VIEW_BOX_HEIGHT, VIEW_BOX_WIDTH, projectCoordinates } from './projectCoordinates';
const MAX_STOPS = 3;
const TEAL_GRID = cltAccentTokens.skyline;
// Route polyline + numbered stops use the national Electric Amber accent
// (DS v1.1.0 cross-city pop) on the dark teal grid background.
const AMBER = lociAmber;
const STOP_RADIUS = 22; // 44px touch target diameter
const STOP_NUMBER_FONT = 22;
// Safely above the max possible polyline length in viewBox units. The
// viewBox is 800×500, so the longest possible stroke is √(800² + 500²) ≈ 943.
// 1200 guarantees the line is fully hidden at animation start even on
// edge-case corner-to-corner routes.
const DRAW_DASH_OFFSET = 1200;
/**
 * Detect `prefers-reduced-motion: reduce` so we can skip the polyline
 * draw-on-mount animation. SSR-safe: defaults to `false` until the client
 * mounts and reads `matchMedia`.
 */
function useReducedMotion() {
    const [reduced, setReduced] = useState(() => {
        if (typeof window === 'undefined')
            return false;
        return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    });
    React.useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        if (!mq)
            return;
        const handler = (e) => setReduced(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);
    return reduced;
}
/**
 * Format a distance in miles to a single decimal place. Trailing zero is kept
 * (e.g. `2.0`) so the chip width stays consistent across renders.
 */
function formatDistanceMiles(miles) {
    return miles.toFixed(1);
}
export function RouteMap({ stops, distanceMiles, chipLabel = 'TONIGHT', onStopClick, highlightedStopId = null, className, style, }) {
    const reduced = useReducedMotion();
    const reactId = useId();
    const patternId = `route-map-grid-${reactId.replace(/:/g, '')}`;
    // Calendar view caps the route at 3 stops; clip defensively here too so the
    // numbered labels never exceed the design.
    const capped = useMemo(() => stops.slice(0, MAX_STOPS), [stops]);
    const projected = useMemo(() => projectCoordinates(capped), [capped]);
    // Render nothing when there's nothing to draw — the consumer is expected to
    // surface its own fallback (per FRE-770 AC: "Empty state (0–1 saved
    // events) — route map hidden, list view fallback rendered"). We treat <2
    // stops as "no route" because a single point is not a route.
    if (projected.length < 2)
        return null;
    const polylinePoints = projected.map((p) => `${p.x},${p.y}`).join(' ');
    const containerClasses = ['relative overflow-hidden rounded-2xl', 'bg-[#0F141F]', className]
        .filter(Boolean)
        .join(' ');
    // Distance label fallback: when the server didn't provide a value we still
    // render the chip with just `{n} STOPS` so the surface doesn't visually
    // collapse on cache-miss.
    const distanceLabel = typeof distanceMiles === 'number' && Number.isFinite(distanceMiles)
        ? `${formatDistanceMiles(distanceMiles)} MI`
        : null;
    return (_jsxs("div", { className: containerClasses, style: style, "data-testid": "route-map", "data-stop-count": projected.length, children: [_jsxs("svg", { viewBox: `0 0 ${VIEW_BOX_WIDTH} ${VIEW_BOX_HEIGHT}`, preserveAspectRatio: "xMidYMid meet", role: "img", "aria-label": `Tonight's route — ${projected.length} stops`, className: "block h-full w-full", "data-testid": "route-map-svg", children: [_jsx("defs", { children: _jsx("pattern", { id: patternId, x: "0", y: "0", width: "32", height: "32", patternUnits: "userSpaceOnUse", children: _jsx("circle", { cx: "2", cy: "2", r: "1.4", fill: TEAL_GRID, fillOpacity: 0.08 }) }) }), _jsx("rect", { width: VIEW_BOX_WIDTH, height: VIEW_BOX_HEIGHT, fill: `url(#${patternId})` }), _jsx("polyline", { points: polylinePoints, fill: "none", stroke: AMBER, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", strokeDasharray: "6 4", "data-testid": "route-map-polyline", "data-reduced-motion": reduced ? 'true' : 'false', style: reduced
                            ? undefined
                            : {
                                strokeDashoffset: 0,
                                animation: 'fs-route-map-draw 600ms ease-out',
                            } }), projected.map((point, index) => {
                        const stop = capped[index];
                        const isHighlighted = highlightedStopId === stop.id;
                        const handleActivate = () => onStopClick?.(stop.id);
                        return (_jsxs("g", { "data-testid": "route-map-stop", "data-stop-id": stop.id, "data-stop-index": index + 1, "data-highlighted": isHighlighted ? 'true' : 'false', transform: `translate(${point.x} ${point.y})`, children: [isHighlighted && (_jsx("circle", { r: STOP_RADIUS + 6, fill: AMBER, fillOpacity: 0.18, "data-testid": "route-map-stop-halo" })), _jsx("circle", { r: STOP_RADIUS, fill: AMBER, stroke: "#0F141F", strokeWidth: 3 }), _jsx("text", { x: 0, y: 0, textAnchor: "middle", dominantBaseline: "central", fill: "#0F141F", fontSize: STOP_NUMBER_FONT, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 600, "aria-hidden": "true", children: index + 1 }), _jsx("foreignObject", { x: -STOP_RADIUS, y: -STOP_RADIUS, width: STOP_RADIUS * 2, height: STOP_RADIUS * 2, children: _jsx("button", { type: "button", onClick: handleActivate, "aria-label": `Stop ${index + 1} — ${stop.title}`, "data-testid": `route-map-stop-button-${stop.id}`, className: "block h-full w-full cursor-pointer rounded-full border-0 bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F141F]" }) })] }, stop.id));
                    })] }), _jsx("div", { className: "pointer-events-none absolute left-3 top-3", children: _jsx(GlassPill, { variant: "strong", "data-testid": "route-map-chip", children: _jsxs(Coord, { size: "sm", color: "inherit", style: { color: '#FFFFFF' }, children: [chipLabel, " \u00B7 ", projected.length, " STOPS", distanceLabel ? ` · ${distanceLabel}` : ''] }) }) }), !reduced && (_jsx("style", { children: `
          @keyframes fs-route-map-draw {
            from { stroke-dashoffset: ${DRAW_DASH_OFFSET}; }
            to   { stroke-dashoffset: 0; }
          }
        ` }))] }));
}
RouteMap.displayName = 'RouteMap';
export default RouteMap;
